import { IonButton, IonButtons, IonIcon, IonSpinner } from "@ionic/react";
import { closeOutline, expandOutline } from "ionicons/icons";
import { useEffect, useRef, useState } from "react";
import { apiFetch } from "../api/client.js";

function clampScale(value) {
  return Math.min(3, Math.max(0.6, value));
}

function touchDistance(touches) {
  if (touches.length < 2) {
    return 0;
  }

  const [first, second] = touches;

  return Math.hypot(first.clientX - second.clientX, first.clientY - second.clientY);
}

export default function PdfDocumentPreview({
  documentId,
  endpointBase,
  isPreparing = false,
  emptyMessage,
  preparingTitle = "Preparing PDF preview",
  preparingText = "Generating the document and loading the real PDF.",
}) {
  const containerRef = useRef(null);
  const pinchRef = useRef({ distance: 0, scale: 1 });
  const [scale, setScale] = useState(1);
  const [pdfData, setPdfData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!documentId) {
      setPdfData(null);

      return undefined;
    }

    let isCancelled = false;

    async function loadPdf() {
      setIsLoading(true);
      setError("");
      setPdfData(null);

      try {
        const blob = await apiFetch(`${endpointBase}/${documentId}`);
        const data = await blob.arrayBuffer();

        if (!isCancelled) {
          setPdfData(data);
        }
      } catch (loadError) {
        if (!isCancelled) {
          setError(loadError.message || "PDF preview failed.");
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    }

    loadPdf();

    return () => {
      isCancelled = true;
    };
  }, [documentId, endpointBase]);

  useEffect(() => {
    if (!pdfData || !containerRef.current) {
      return undefined;
    }

    let isCancelled = false;
    const container = containerRef.current;

    async function renderPdf() {
      setIsLoading(true);
      setError("");
      container.replaceChildren();

      try {
        const [pdfjs, worker] = await Promise.all([
          import("pdfjs-dist"),
          import("pdfjs-dist/build/pdf.worker.min.mjs?url"),
        ]);

        pdfjs.GlobalWorkerOptions.workerSrc = worker.default;

        const pdf = await pdfjs.getDocument({ data: pdfData.slice(0) }).promise;

        for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
          if (isCancelled) {
            return;
          }

          const page = await pdf.getPage(pageNumber);
          const viewport = page.getViewport({ scale });
          const canvas = document.createElement("canvas");
          const context = canvas.getContext("2d");
          const pixelRatio = window.devicePixelRatio || 1;

          canvas.className = "pdf-preview-page";
          canvas.width = Math.floor(viewport.width * pixelRatio);
          canvas.height = Math.floor(viewport.height * pixelRatio);
          canvas.style.width = `${viewport.width}px`;
          canvas.style.height = `${viewport.height}px`;

          context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
          container.appendChild(canvas);

          await page.render({ canvasContext: context, viewport }).promise;
        }
      } catch (renderError) {
        if (!isCancelled) {
          setError(renderError.message || "PDF preview failed.");
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    }

    renderPdf();

    return () => {
      isCancelled = true;
    };
  }, [pdfData, scale]);

  if (isPreparing) {
    return (
      <div className="pdf-preview-status">
        <IonSpinner name="crescent" />
        <strong>{preparingTitle}</strong>
        <span>{preparingText}</span>
      </div>
    );
  }

  if (!documentId) {
    return <div className="invoice-preview empty">{emptyMessage}</div>;
  }

  return (
    <section
      className={`pdf-preview ${isFullscreen ? "pdf-preview-fullscreen" : ""}`}
      onTouchStart={(event) => {
        const distance = touchDistance(event.touches);

        if (distance) {
          pinchRef.current = { distance, scale };
        }
      }}
      onTouchMove={(event) => {
        const distance = touchDistance(event.touches);

        if (!distance || !pinchRef.current.distance) {
          return;
        }

        event.preventDefault();
        setScale(clampScale(pinchRef.current.scale * (distance / pinchRef.current.distance)));
      }}
    >
      <div className="pdf-preview-toolbar">
        <strong>PDF Preview</strong>
        <IonButtons>
          <IonButton
            size="small"
            fill="outline"
            onClick={() => setIsFullscreen((value) => !value)}
            aria-label={isFullscreen ? "Close fullscreen PDF preview" : "Open fullscreen PDF preview"}
          >
            <IonIcon icon={isFullscreen ? closeOutline : expandOutline} />
          </IonButton>
          <IonButton size="small" fill="outline" onClick={() => setScale((value) => clampScale(value - 0.2))}>
            -
          </IonButton>
          <span>{Math.round(scale * 100)}%</span>
          <IonButton size="small" fill="outline" onClick={() => setScale((value) => clampScale(value + 0.2))}>
            +
          </IonButton>
        </IonButtons>
      </div>

      {isLoading && (
        <div className="pdf-preview-loading">
          <IonSpinner name="crescent" />
          <strong>Rendering PDF</strong>
        </div>
      )}
      {error && <p className="pdf-preview-error">{error}</p>}
      <div className="pdf-preview-canvas" ref={containerRef} />
    </section>
  );
}

